import { layoutWithLines, prepareWithSegments } from '@chenglou/pretext'
export const LINE_HEIGHT = 19.2
// Extra subpixel allowance keeps the painted gap at least 12px after browser rounding.
const CLEARANCE = 12.05
const clamp = (value: number) => Math.max(0, Math.min(1, value))
export function separation(y: number, heroTop: number, heroBottom: number) {
  const ramp = 3 * LINE_HEIGHT
  const depth = clamp(
    Math.min(
      (y + LINE_HEIGHT - (heroTop - 42)) / ramp,
      (heroBottom + 36 - y) / ramp,
    ),
  )
  const c = clamp((depth - 0.2) / 0.8)
  return c < 0.5 ? 4 * c ** 3 : 1 - (-2 * c + 2) ** 3 / 2
}
export function halfChord(y: number, center: number, radius: number) {
  const distance = Math.max(
    0,
    Math.abs(y + LINE_HEIGHT / 2 - center) - LINE_HEIGHT / 2,
  )
  return Math.sqrt(Math.max(0, radius ** 2 - distance ** 2))
}
export function prepareLines(paragraphs: string[], font: string) {
  const widthOf = (text: string) =>
    layoutWithLines(prepareWithSegments(text, font), 10000, LINE_HEIGHT)
      .lines[0]?.width ?? 0
  let offset = 0
  return paragraphs.flatMap((paragraph) => {
    const { lines } = layoutWithLines(
      prepareWithSegments(paragraph, font),
      380,
      LINE_HEIGHT,
    )
    const result = lines.map((line) => {
      const halves = layoutWithLines(
        prepareWithSegments(line.text, font),
        190,
        LINE_HEIGHT,
      ).lines
      const left = (halves[0]?.text ?? line.text).trimEnd()
      const right = line.text.slice(left.length).trimStart()
      const leftWidth = widthOf(left)
      const rightWidth = widthOf(right)
      const result = {
        left,
        right,
        leftWidth,
        rightWidth,
        width: line.width,
        offset,
      }
      offset += LINE_HEIGHT
      return result
    })
    offset += LINE_HEIGHT
    return result
  })
}
export function mountReflow(
  container: HTMLElement,
  hero: HTMLElement,
  layer: HTMLElement,
  button: HTMLElement,
  paragraphs: string[],
) {
  let disposed = false
  let frame = 0
  let lines: ReturnType<typeof prepareLines> = []
  let elements: { left: HTMLSpanElement; right: HTMLSpanElement }[] = []
  const media = window.matchMedia('(min-width: 1024px)')
  const draw = () => {
    frame = 0
    if (disposed || !media.matches || !lines.length) return
    const started = performance.now()
    const vh = window.innerHeight
    const vw = window.innerWidth
    const rect = hero.getBoundingClientRect()
    // The reference uses the viewport hero bounds even before the stage sticks.
    const top = rect.top
    const radius = rect.width / 2
    const bottom = top + rect.height
    const center = vw / 2
    const origin = bottom + 20
    const textHeight = lines.at(-1)!.offset + LINE_HEIGHT
    const height = Math.max(
      vh + origin + textHeight + 72 + 0.32 * vh,
      Math.round(1.75 * vh),
    )
    container.style.height = `${height}px`
    const scroll = Math.max(
      0,
      Math.min(-container.getBoundingClientRect().top, height - vh),
    )
    const gutter = vw >= 1280 ? 80 : 40
    lines.forEach((line, index) => {
      const { left, right } = elements[index]!
      const y = origin + line.offset - scroll
      const visible = y >= -2 * LINE_HEIGHT && y <= vh + 2 * LINE_HEIGHT
      left.hidden = right.hidden = !visible
      if (!visible) return
      const r = separation(y, top, bottom)
      const chord = halfChord(y, top + radius, radius)
      const start = center - line.width / 2
      const rightStart = start + line.width - line.rightWidth
      const leftTarget =
        Math.max(gutter, center - chord - CLEARANCE - 190) +
        190 -
        line.leftWidth
      const rightTarget = Math.max(
        gutter,
        Math.min(center + chord + CLEARANCE, vw - gutter - 190),
      )
      let lx = start + (leftTarget - start) * r
      let rx = rightStart + (rightTarget - rightStart) * r
      // Reach full clearance before touching the circle, without a tangent snap.
      const outside = Math.max(top - y - LINE_HEIGHT, y - bottom, 0)
      const approach = clamp(1 - outside / 36)
      const safety = approach * approach * (3 - 2 * approach)
      lx +=
        (Math.min(lx, center - chord - CLEARANCE - line.leftWidth) - lx) *
        safety
      rx += (Math.max(rx, center + chord + CLEARANCE) - rx) * safety
      left.style.transform = `translate3d(${lx}px,${y}px,0)`
      right.style.transform = `translate3d(${rx}px,${y}px,0)`
    })
    button.style.top = `${Math.max(origin + textHeight + 24 - scroll, bottom + 24)}px`
    container.dataset.ready = 'true'
    if (performance.getEntriesByName('museum-reflow').length >= 600)
      performance.clearMeasures('museum-reflow')
    performance.measure('museum-reflow', {
      start: started,
      end: performance.now(),
    })
  }
  const schedule = () => {
    if (!frame && !disposed) frame = requestAnimationFrame(draw)
  }
  const ready = () => {
    if (disposed) return
    const style = getComputedStyle(layer)
    lines = prepareLines(paragraphs, `${style.fontSize} ${style.fontFamily}`)
    elements = lines.map((line) => {
      const left = document.createElement('span')
      const right = document.createElement('span')
      left.textContent = line.left
      right.textContent = line.right
      layer.append(left, right)
      return { left, right }
    })
    schedule()
  }
  void document.fonts.ready.then(ready)
  const observer = new ResizeObserver(schedule)
  observer.observe(hero)
  window.addEventListener('scroll', schedule, { passive: true })
  window.addEventListener('resize', schedule)
  media.addEventListener('change', schedule)
  return () => {
    disposed = true
    cancelAnimationFrame(frame)
    observer.disconnect()
    window.removeEventListener('scroll', schedule)
    window.removeEventListener('resize', schedule)
    media.removeEventListener('change', schedule)
    layer.replaceChildren()
    performance.clearMeasures('museum-reflow')
  }
}
